import { GATEWAY_CONDITION_TONE, type Tone } from '@constants/tones';
import { CONDITION_LABEL, ISSUE_LABEL, ISSUE_SENTENCE } from '@locales/id/readers';
import type { Gateway, GatewayCondition, GatewayIssue, GatewayState } from '@lib/api';
import { num, wibTime } from '@lib/format';

/**
 * The condition of a reader whose payload carries only its connection state (the overview's map): disconnected,
 * never heard from, or connected. It claims no issue; wherever the server sends `condition`, that one is used.
 */
export const conditionFromState = (state: GatewayState): GatewayCondition => (state === 'offline' ? 'offline' : state === 'online' ? 'normal' : 'never_connected');

/** The server's condition when the payload has it (every gateway answer of the reader pages), else the state's. */
export const conditionOf = (g: { state: GatewayState; condition?: GatewayCondition }): GatewayCondition => g.condition ?? conditionFromState(g.state);

export interface ConditionView { condition: GatewayCondition; label: string; tone: Tone }
export const conditionView = (condition: GatewayCondition): ConditionView => ({ condition, label: CONDITION_LABEL[condition], tone: GATEWAY_CONDITION_TONE[condition] });

/** The issue that names a reader's problem in one line (the list's badge line): the most serious one. */
export const leadIssue = (issues: readonly GatewayIssue[] | undefined): string | null => (issues?.length ? ISSUE_LABEL[issues[0]] : null);

/** Since when a reader has been silent: its last heartbeat, else when it was marked disconnected. */
const silentSince = (g: Pick<Gateway, 'lastHeartbeatAt' | 'stateAt'>) => g.lastHeartbeatAt ?? g.stateAt;

/**
 * The reader page's banner: the server's issues, each in one sentence, most serious first. Nothing for a normal
 * reader; one sentence for a reader that never connected.
 */
export function issueSentences(g: Pick<Gateway, 'condition' | 'issues' | 'lastHeartbeatAt' | 'stateAt' | 'outboxPending'>): string[] {
  if (g.condition === 'normal') return [];
  if (g.condition === 'never_connected') return [ISSUE_SENTENCE.never];
  const since = silentSince(g);
  return g.issues.map((i) => {
    switch (i) {
      case 'OFFLINE':
        return ISSUE_SENTENCE.OFFLINE(since ? wibTime(since) : null);
      case 'NOT_RESPONDING':
        return ISSUE_SENTENCE.NOT_RESPONDING(since ? wibTime(since) : null);
      case 'OUTBOX_BACKLOG':
        return ISSUE_SENTENCE.OUTBOX_BACKLOG(g.outboxPending ? num(g.outboxPending) : null);
      default:
        return ISSUE_SENTENCE[i];
    }
  });
}
